/**
 * "Nova cotação" da aba Valores (PoolTrack, 04/10; escopo revisto pelo
 * operador no mesmo dia): a pessoa VÊ o km de cada perna, digita o preço por
 * km, ajusta os endereços e as posições no mapa, e lança.
 *
 *   ida:   A origem (de onde o técnico sai)  →  B destino (o endereço do serviço)
 *   volta: B destino  →  C retorno (padrão: a própria origem)
 *
 * O provedor só é consultado no "Calcular" e no fim de um arraste no mapa —
 * nunca a cada tecla (cada consulta é cobrada). O texto de um ponto que mudou
 * depois do cálculo invalida a perna daquele ponto até recalcular; o "Lançar"
 * manda os RECIBOS das pernas calculadas (o servidor grava os metros do recibo,
 * não os da tela). Sem provedor configurado, o km de cada perna é digitado com
 * motivo.
 *
 * Limitado ao Brasil (operador, 04/10): marcador solto fora do Brasil volta
 * para onde estava, sem consultar o provedor (o servidor também recusa); perna
 * acima de 500 km mostra um aviso de conferência, sem bloquear.
 *
 * Autocompletar (operador, 05/10): Origem, Destino e Retorno sugerem endereços
 * enquanto a pessoa digita (quote-address-input). A sugestão escolhida vai ao
 * "Calcular" como `place` — quem acha a posição é o servidor, e o token da
 * sessão sai UMA vez (takePlaceInput). Sem sugestões disponíveis, o campo é o
 * texto livre de antes, sem aviso. O id do lugar escolhido vai junto no
 * "Lançar" (fica gravado para sempre; a posição, não — F-112).
 *
 * Mapa do Google (F-112): a chave do navegador vem da API quando o diálogo
 * abre. Sem chave, script bloqueado ou chave recusada: sem mapa, uma linha
 * discreta, e todo o resto (endereços, Calcular, km, preço, Lançar, o link da
 * rota no Google Maps) funciona igual.
 *
 * Valor cheio (PoolTrack, 05/10): a linha do total tem um campo para o valor
 * cheio do deslocamento (ida e volta). Vazio, a cotação é por km, como antes.
 * Digitado, ELE é o total (quotePricing, a mesma regra do servidor): o preço
 * por km fica opcional e só de referência, e mudá-lo depois não mexe no total
 * — a tela diz isso com todas as letras. Apagar o valor cheio volta ao por km.
 * O campo só aparece quando a API diz que grava o valor cheio
 * (quoteFullAmountEnabled): o SPA publica antes da API, e a API anterior
 * descartaria o valor calada e lançaria por km.
 */
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calculator, ExternalLink, MapPin, TriangleAlert } from 'lucide-react';
import {
  GEO_PRECISION_LABELS,
  QUOTE_LEG_LABELS,
  QUOTE_LONG_LEG_WARNING,
  QUOTE_POINT_LABELS,
  QUOTE_POINT_WARNING_LABELS,
  QUOTE_FULL_AMOUNT_MAX_CENTS,
  chargedKm,
  googleMapsDirectionsUrl,
  isInBrazil,
  isLongLeg,
  legSubtotalCents,
  metersFromKmInput,
  quoteMoneyCents,
  quotePricing,
  takePlaceInput,
  type GeoPrecision,
  type QuoteCalculateResponse,
  type QuoteLegKey,
  type QuoteLegRoute,
  type QuotePointInput,
  type QuotePlaceChoice,
  type QuotePointKey,
  type QuotePointWarning,
  type TicketDetail,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label } from '@/components/ui/input';
import { Badge, Dialog, Spinner } from '@/components/ui/misc';
import { centsToMoney, formatBRL, parseBRLInput } from '@/features/billing/billing-format';
import { MoneyInput } from '@/features/billing/contract-drawer';
import { fetchQuoteSuggestions, useCalculateQuote, useCreateQuote } from './tickets-mutations';
import { quoteMapConfigQuery } from './tickets-queries';
import { AddressInput, type SuggestFn } from './quote-address-input';
import {
  QUOTE_POINT_COLORS,
  QUOTE_POINT_LETTERS,
  QuoteMap,
  type QuoteMapLeg,
  type QuoteMapPoint,
} from './quote-map';
import {
  QUOTE_OUTSIDE_BRAZIL_MESSAGE,
  formatDuration,
  formatKm,
  quoteErrorMessage,
} from './quote-format';
import { destinationOf } from './ticket-sheet-view';

interface PointState {
  /** o que está no campo */
  text: string;
  /** o texto que produziu a posição atual (null = nunca resolvido) */
  resolvedText: string | null;
  lat: number | null;
  lng: number | null;
  address: string | null;
  precision: GeoPrecision | null;
  warnings: QuotePointWarning[];
  /** a sugestão escolhida no autocompletar (vale enquanto o texto for o dela) */
  place: QuotePlaceChoice | null;
  /** o lugar escolhido que PRODUZIU a posição atual (null = texto livre ou arrastado) */
  placeId: string | null;
}

interface LegState {
  manual: boolean;
  route:
    | (QuoteLegRoute & { from: { lat: number; lng: number }; to: { lat: number; lng: number } })
    | null;
  manualKm: string;
  reason: string;
  price: string;
}

type Points = Record<QuotePointKey, PointState>;
type Legs = Record<QuoteLegKey, LegState>;

const emptyPoint = (text = ''): PointState => ({
  text,
  resolvedText: null,
  lat: null,
  lng: null,
  address: null,
  precision: null,
  warnings: [],
  place: null,
  placeId: null,
});

const emptyLeg = (manual: boolean): LegState => ({
  manual,
  route: null,
  manualKm: '',
  reason: '',
  price: '',
});

/**
 * O destino que a cotação sugere: o endereço EFETIVO da ficha (spec 031) — o
 * editado vence a descrição. A regra mora em ticket-sheet-view (destinationOf).
 */

/** o preço digitado num campo de dinheiro: vazio, inválido (ou negativo), ou o decimal do fio */
type MoneyField = { kind: 'empty' } | { kind: 'invalid' } | { kind: 'ok'; value: string };
function moneyField(raw: string): MoneyField {
  if (raw.trim() === '') return { kind: 'empty' };
  const value = parseBRLInput(raw);
  return value === null || value.startsWith('-') ? { kind: 'invalid' } : { kind: 'ok', value };
}

/** "12,5" → "12.5" (até 3 casas); null se inválido */
function kmInput(raw: string): string | null {
  const s = raw.trim().replace(',', '.');
  return /^\d{1,5}(\.\d{1,3})?$/.test(s) ? s : null;
}

const hasPos = (p: PointState): p is PointState & { lat: number; lng: number } =>
  p.lat !== null && p.lng !== null;
const fresh = (p: PointState) => hasPos(p) && p.text.trim() === p.resolvedText;
const same = (a: { lat: number; lng: number }, b: { lat: number | null; lng: number | null }) =>
  a.lat === b.lat && a.lng === b.lng;
/** o texto do campo ainda é o da sugestão escolhida? (digitar de novo volta ao texto livre) */
const chosen = (p: PointState): p is PointState & { place: QuotePlaceChoice } =>
  p.place !== null && p.text.trim() === p.place.label.trim();

/** as pontas de cada perna com o estado atual dos pontos */
function legEnds(
  points: Points,
  returnSame: boolean
): Record<QuoteLegKey, [PointState, PointState]> {
  const back = returnSame ? points.origin : points.returnTo;
  return { ida: [points.origin, points.destination], volta: [points.destination, back] };
}

export function QuoteDialog({
  ticket,
  mapsAvailable,
  fullAmountAvailable,
  onClose,
}: {
  ticket: TicketDetail;
  mapsAvailable: boolean;
  /** a API grava o valor cheio (quoteFullAmountEnabled); sem isto, o campo não aparece */
  fullAmountAvailable: boolean;
  onClose: () => void;
}) {
  const calculate = useCalculateQuote(ticket.ticketNumber);
  const create = useCreateQuote(ticket.ticketNumber);

  const [points, setPoints] = useState<Points>(() => ({
    origin: emptyPoint(),
    destination: emptyPoint(destinationOf(ticket)),
    returnTo: emptyPoint(),
  }));
  const [returnSame, setReturnSame] = useState(true);
  // sem provedor, as duas pernas são à mão desde o começo
  const [legs, setLegs] = useState<Legs>(() => ({
    ida: emptyLeg(!mapsAvailable),
    volta: emptyLeg(!mapsAvailable),
  }));
  // o valor cheio do deslocamento, como digitado ('' = não digitado: por km)
  const [fullText, setFullText] = useState('');
  const [fitKey, setFitKey] = useState(0);
  const [calcError, setCalcError] = useState<string | null>(null);
  // autocompletar: ligado com provedor; o primeiro `unavailable` o desliga
  // até o diálogo fechar (Places API não habilitada não muda numa tecla)
  const [autocompleteOn, setAutocompleteOn] = useState(mapsAvailable);

  const ends = legEnds(points, returnSame);

  const setPoint = (key: QuotePointKey, patch: Partial<PointState>) =>
    setPoints((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const setLeg = (key: QuoteLegKey, patch: Partial<LegState>) =>
    setLegs((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  /** a perna calculada ainda vale para os pontos atuais? */
  const routeFresh = (key: QuoteLegKey): boolean => {
    const r = legs[key].route;
    const [a, b] = ends[key];
    return r !== null && fresh(a) && fresh(b) && same(r.from, a) && same(r.to, b);
  };

  /** km cobrado da perna ("11.1"), ou null quando ainda não há */
  const legKm = (key: QuoteLegKey): string | null => {
    const leg = legs[key];
    if (leg.manual) {
      const km = kmInput(leg.manualKm);
      return km === null ? null : chargedKm(metersFromKmInput(km));
    }
    return routeFresh(key) ? leg.route!.km : null;
  };

  const legCents = (key: QuoteLegKey): number | null => {
    const km = legKm(key);
    const price = moneyField(legs[key].price);
    if (km === null || price.kind !== 'ok') return null;
    return legSubtotalCents(km, price.value);
  };

  const idaCents = legCents('ida');
  const voltaCents = legCents('volta');

  // ── valor cheio × por km: a regra única (quotePricing) ──────────────────
  // digitado = a pessoa escolheu o valor cheio, mesmo que ainda esteja errado
  // (aí não há total, e o erro aparece) — nunca cai para o por km calado
  const fullTyped = fullAmountAvailable && fullText.trim() !== '';
  const full = moneyField(fullText);
  const fullCents = full.kind === 'ok' ? quoteMoneyCents(full.value) : null;
  const fullProblem = !fullTyped
    ? null
    : fullCents === null
      ? 'valor inválido'
      : fullCents <= 0
        ? 'o valor cheio tem de ser maior que zero'
        : fullCents > QUOTE_FULL_AMOUNT_MAX_CENTS
          ? `acima do teto de ${formatBRL(centsToMoney(QUOTE_FULL_AMOUNT_MAX_CENTS))}`
          : null;
  const pricing = !fullTyped
    ? quotePricing({ idaCents, voltaCents, fullCents: null })
    : fullProblem === null
      ? quotePricing({ idaCents, voltaCents, fullCents })
      : null;
  const fullMode = fullTyped;
  const totalCents = pricing?.totalCents ?? null;
  /** pelo km daria — a referência, com as duas pernas com km e preço */
  const perKmCents = idaCents !== null && voltaCents !== null ? idaCents + voltaCents : null;

  // ── autocompletar ────────────────────────────────────────────────────────
  // o viés das sugestões: o ponto já posicionado (destino, senão origem). Por
  // ref, para a função de consulta não mudar a cada render
  const nearRef = useRef<{ lat: number; lng: number } | null>(null);
  nearRef.current = hasPos(points.destination)
    ? { lat: points.destination.lat, lng: points.destination.lng }
    : hasPos(points.origin)
      ? { lat: points.origin.lat, lng: points.origin.lng }
      : null;
  const suggest = useMemo<SuggestFn | null>(
    () =>
      autocompleteOn
        ? (input, sessionToken, signal) =>
            fetchQuoteSuggestions(
              ticket.ticketNumber,
              { input, sessionToken, near: nearRef.current },
              signal
            )
        : null,
    [autocompleteOn, ticket.ticketNumber]
  );
  const onUnavailable = () => setAutocompleteOn(false);
  const onPick = (key: QuotePointKey, choice: QuotePlaceChoice) =>
    setPoint(key, { text: choice.label, place: choice });

  // ── Calcular / arraste ──────────────────────────────────────────────────
  function inputFor(p: PointState, reverse: boolean): QuotePointInput | null {
    if (hasPos(p) && (reverse || fresh(p))) {
      return { kind: 'pin', lat: p.lat, lng: p.lng, reverse };
    }
    // sugestão escolhida: o servidor resolve o lugar (nunca a posição daqui)
    if (chosen(p)) return takePlaceInput(p.place).input;
    const text = p.text.trim();
    return text.length >= 3 ? { kind: 'address', text } : null;
  }

  function run(planned: Points, legKeys: QuoteLegKey[], drag: QuotePointKey | null) {
    setCalcError(null);
    const origin = inputFor(planned.origin, drag === 'origin');
    const destination = inputFor(planned.destination, drag === 'destination');
    const returnTo = returnSame ? null : inputFor(planned.returnTo, drag === 'returnTo');
    if (!origin || !destination || (!returnSame && !returnTo)) {
      setCalcError('Informe os endereços (pelo menos 3 letras) antes de calcular.');
      return;
    }
    // o token de cada sugestão escolhida sai AGORA, uma vez: o ponto fica sem
    // ele, e um "Calcular" repetido (depois de uma falha) manda o lugar sem token
    const next: Points = { ...planned };
    const sentInputs: Record<QuotePointKey, QuotePointInput | null> = {
      origin,
      destination,
      returnTo,
    };
    for (const key of QUOTE_POINT_KEYS) {
      const p = next[key];
      if (sentInputs[key]?.kind === 'place' && p.place) {
        next[key] = { ...p, place: takePlaceInput(p.place).rest };
      }
    }
    setPoints(next);
    calculate.mutate(
      { origin, destination, returnTo, legs: legKeys },
      {
        onSuccess: (res: QuoteCalculateResponse) => {
          const updated: Points = { ...next };
          for (const key of QUOTE_POINT_KEYS) {
            const r = key === 'returnTo' ? res.points.returnTo : res.points[key];
            if (!r || !r.resolved) continue;
            const sent = sentInputs[key];
            const base = updated[key];
            // reversa (arraste) traz o texto do endereço; geocodificação mantém o digitado
            const text = r.precision === 'pin' ? (r.address ?? base.text) : base.text;
            updated[key] = {
              text,
              resolvedText: text.trim(),
              lat: r.lat,
              lng: r.lng,
              address: r.address,
              precision: r.precision,
              warnings: r.warnings,
              // resolvido: daqui em diante o ponto vai como posição (pin)
              place: null,
              // a posição saiu do lugar escolhido? (texto livre e arraste: não)
              placeId: sent?.kind === 'place' ? sent.placeId : null,
            };
          }
          setPoints(updated);
          const after = legEnds(updated, returnSame);
          setLegs((prev) => {
            const out = { ...prev };
            for (const key of QUOTE_LEG_KEYS) {
              const route = res.legs[key];
              const [a, b] = after[key];
              if (route && hasPos(a) && hasPos(b)) {
                out[key] = {
                  ...prev[key],
                  route: {
                    ...route,
                    from: { lat: a.lat, lng: a.lng },
                    to: { lat: b.lat, lng: b.lng },
                  },
                };
              }
            }
            return out;
          });
          if (drag === null) setFitKey((k) => k + 1);
        },
        onError: (err) => setCalcError(quoteErrorMessage(err)),
      }
    );
  }

  const routeLegs = QUOTE_LEG_KEYS.filter((k) => !legs[k].manual);

  const onCalculate = () => {
    if (routeLegs.length === 0) return;
    run(points, routeLegs, null);
  };

  /** fim de um arraste; false = recusado (fora do Brasil), sem consultar o provedor */
  const onMove = (key: QuotePointKey, lat: number, lng: number): boolean => {
    if (!isInBrazil(lat, lng)) {
      setCalcError(QUOTE_OUTSIDE_BRAZIL_MESSAGE);
      return false;
    }
    const next: Points = {
      ...points,
      // a posição passa a ser da pessoa: o lugar escolhido não a descreve mais
      [key]: { ...points[key], lat, lng, precision: 'pin' as const, warnings: [], placeId: null },
    };
    setPoints(next);
    const affected: QuoteLegKey[] =
      key === 'destination'
        ? ['ida', 'volta']
        : key === 'returnTo'
          ? ['volta']
          : returnSame
            ? ['ida', 'volta']
            : ['ida'];
    const toRun = affected.filter((k) => !legs[k].manual);
    if (toRun.length > 0) run(next, toRun, key);
    else setCalcError(null);
    return true;
  };

  // ── mapa ─────────────────────────────────────────────────────────────────
  const mapPoints = useMemo<QuoteMapPoint[]>(
    () =>
      (returnSame ? (['origin', 'destination'] as const) : QUOTE_POINT_KEYS)
        .map((key) => ({ key, p: points[key] }))
        .filter(({ p }) => hasPos(p))
        .map(({ key, p }) => ({ key, lat: p.lat!, lng: p.lng! })),
    [points, returnSame]
  );
  const mapLegs = useMemo<QuoteMapLeg[]>(
    () =>
      QUOTE_LEG_KEYS.flatMap((key) => {
        const r = legs[key].route;
        if (!r || legs[key].manual) return [];
        return [{ key, path: r.path, from: r.from, to: r.to }];
      }),
    [legs]
  );
  // a chave do NAVEGADOR, da API (nunca do build); sem cálculo, nem pergunta
  const mapConfigQ = useQuery({
    ...quoteMapConfigQuery(ticket.ticketNumber),
    enabled: mapsAvailable,
  });
  const [mapFailed, setMapFailed] = useState(false);
  const browserKey = mapConfigQ.data?.browserKey ?? null;
  const mapState: 'off' | 'loading' | 'on' | 'unavailable' = !mapsAvailable
    ? 'off'
    : mapConfigQ.isPending
      ? 'loading'
      : browserKey !== null && !mapFailed
        ? 'on'
        : 'unavailable';

  const directionsUrl =
    hasPos(points.origin) && hasPos(points.destination)
      ? googleMapsDirectionsUrl({
          origin: points.origin,
          destination: points.destination,
          returnTo: returnSame || !hasPos(points.returnTo) ? points.origin : points.returnTo,
        })
      : null;

  // ── Lançar ───────────────────────────────────────────────────────────────
  const legProblem = (key: QuoteLegKey): string | null => {
    const leg = legs[key];
    const price = moneyField(leg.price);
    if (leg.manual) {
      if (kmInput(leg.manualKm) === null) return 'informe o km';
      if (leg.reason.trim().length < 5) return 'informe o motivo do km à mão';
    } else if (!routeFresh(key)) {
      return leg.route ? 'endereço alterado — calcule de novo' : 'calcule o km';
    }
    if (price.kind === 'invalid') return 'preço por km inválido';
    // com o valor cheio, o preço por km é opcional (só referência)
    if (price.kind === 'empty' && !fullMode) {
      return fullAmountAvailable
        ? 'informe o preço por km, ou o valor cheio no total'
        : 'informe o preço por km';
    }
    return null;
  };
  const problems = QUOTE_LEG_KEYS.map((k) => legProblem(k));
  const canLaunch =
    problems.every((p) => p === null) &&
    fullProblem === null &&
    totalCents !== null &&
    totalCents > 0;

  const savedPoint = (p: PointState) => ({
    input: p.text.trim() || null,
    address: p.address,
    lat: p.lat,
    lng: p.lng,
    precision: p.precision,
    warnings: p.warnings,
    placeId: p.placeId,
  });

  const onLaunch = () => {
    if (!canLaunch) return;
    const legBody = (key: QuoteLegKey) => {
      const leg = legs[key];
      // vazio só chega aqui com o valor cheio (legProblem barra o por km sem preço)
      const price = moneyField(leg.price);
      const pricePerKm = price.kind === 'ok' ? price.value : null;
      return leg.manual
        ? {
            mode: 'manual' as const,
            km: kmInput(leg.manualKm)!,
            reason: leg.reason.trim(),
            pricePerKm,
          }
        : { mode: 'route' as const, receipt: leg.route!.receipt, pricePerKm };
    };
    create.mutate(
      {
        origin: savedPoint(points.origin),
        destination: savedPoint(points.destination),
        returnTo: returnSame ? null : savedPoint(points.returnTo),
        ida: legBody('ida'),
        volta: legBody('volta'),
        // só com o valor cheio digitado (e a API que o grava); sem ele, por km
        ...(fullMode && full.kind === 'ok' ? { fullAmount: full.value } : {}),
      },
      { onSuccess: onClose }
    );
  };

  const busy = calculate.isPending || create.isPending;

  return (
    <Dialog open onClose={onClose} title="Nova cotação de deslocamento" className="sm:max-w-5xl">
      <div className="space-y-4">
        {!mapsAvailable ? (
          <p className="flex items-start gap-2 rounded-md border border-[#FFE58F] bg-[#FFFBE6] p-3 text-sm text-[#874D00]">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            <span>
              <strong>Cálculo de km indisponível</strong> — o provedor de mapas não está
              configurado. Informe o km de cada perna à mão, com o motivo.
            </span>
          </p>
        ) : null}

        <div
          className={
            mapState === 'on' || mapState === 'loading'
              ? 'grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]'
              : 'grid gap-4'
          }
        >
          {/* ── pontos ─────────────────────────────────────────────── */}
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Técnico responsável:{' '}
              <span className="font-medium text-foreground">
                {ticket.technician?.name ?? 'não definido no ticket'}
              </span>
            </p>
            <PointField
              pointKey="origin"
              label="Origem — de onde o técnico sai"
              placeholder="Rua, número, cidade - UF"
              point={points.origin}
              onText={(text) => setPoint('origin', { text, place: null })}
              onPick={(choice) => onPick('origin', choice)}
              suggest={suggest}
              onUnavailable={onUnavailable}
              mapsAvailable={mapsAvailable}
            />
            <PointField
              pointKey="destination"
              label="Destino — endereço do serviço"
              placeholder="Da descrição do ticket, se houver"
              point={points.destination}
              onText={(text) => setPoint('destination', { text, place: null })}
              onPick={(choice) => onPick('destination', choice)}
              suggest={suggest}
              onUnavailable={onUnavailable}
              mapsAvailable={mapsAvailable}
            />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={returnSame} onCheckedChange={(v) => setReturnSame(v === true)} />
              Volta termina na origem
            </label>
            {!returnSame ? (
              <PointField
                pointKey="returnTo"
                label="Retorno — onde a volta termina"
                placeholder="Rua, número, cidade - UF"
                point={points.returnTo}
                onText={(text) => setPoint('returnTo', { text, place: null })}
                onPick={(choice) => onPick('returnTo', choice)}
                suggest={suggest}
                onUnavailable={onUnavailable}
                mapsAvailable={mapsAvailable}
              />
            ) : null}

            {mapsAvailable ? (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onCalculate}
                  disabled={busy || routeLegs.length === 0}
                >
                  {calculate.isPending ? (
                    <Spinner className="size-4" />
                  ) : (
                    <Calculator className="size-4" />
                  )}
                  Calcular
                </Button>
                {directionsUrl ? (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="size-3.5" />
                    Ver rota no Google Maps
                  </a>
                ) : null}
              </div>
            ) : null}
            {calcError ? <p className="text-sm text-destructive">{calcError}</p> : null}
            {mapState === 'unavailable' ? (
              <p className="text-xs text-muted-foreground" data-testid="quote-map-off">
                Mapa indisponível — endereços, km e preço funcionam normalmente.
              </p>
            ) : null}
          </div>

          {/* ── mapa (do Google) ───────────────────────────────────── */}
          {mapState === 'loading' ? (
            <div className="h-80 w-full rounded-md border bg-muted/40" aria-busy />
          ) : mapState === 'on' && browserKey !== null ? (
            <div className="space-y-1.5">
              <QuoteMap
                browserKey={browserKey}
                mapId={mapConfigQ.data?.mapId ?? null}
                points={mapPoints}
                legs={mapLegs}
                onMove={onMove}
                onUnavailable={() => setMapFailed(true)}
                fitKey={fitKey}
                draggable={!busy}
                className="h-80 w-full overflow-hidden rounded-md border bg-muted/40"
              />
              <p className="text-xs text-muted-foreground">
                Arraste um marcador para corrigir a posição: a perna daquele ponto é recalculada.
                Linha cheia: ida · tracejada: volta.
              </p>
            </div>
          ) : null}
        </div>

        {/* ── pernas ─────────────────────────────────────────────────── */}
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left text-xs">
                <th className="h-9 px-3 font-semibold">Perna</th>
                <th className="h-9 px-3 font-semibold">Km</th>
                <th className="h-9 w-40 px-3 font-semibold">Preço por km</th>
                <th className="h-9 w-32 px-3 text-right font-semibold">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {QUOTE_LEG_KEYS.map((key, i) => {
                const leg = legs[key];
                const [from, to] =
                  key === 'ida'
                    ? (['origin', 'destination'] as const)
                    : (['destination', returnSame ? 'origin' : 'returnTo'] as const);
                const cents = legCents(key);
                const km = legKm(key);
                const r = leg.route;
                return (
                  <tr key={key} className="border-t align-top">
                    <td className="px-3 py-2.5">
                      <div className="font-medium">{QUOTE_LEG_LABELS[key]}</div>
                      <div className="text-xs text-muted-foreground">
                        {QUOTE_POINT_LETTERS[from]} {QUOTE_POINT_LABELS[from].toLowerCase()} →{' '}
                        {QUOTE_POINT_LETTERS[to]} {QUOTE_POINT_LABELS[to].toLowerCase()}
                      </div>
                      {mapsAvailable ? (
                        <label className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Checkbox
                            checked={leg.manual}
                            onCheckedChange={(v) => setLeg(key, { manual: v === true })}
                          />
                          km à mão
                        </label>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5">
                      {leg.manual ? (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <Input
                              aria-label={`Km da ${QUOTE_LEG_LABELS[key].toLowerCase()}`}
                              value={leg.manualKm}
                              onChange={(e) => setLeg(key, { manualKm: e.target.value })}
                              inputMode="decimal"
                              placeholder="0,0"
                              className="h-8 w-24 text-right tabular-nums"
                              aria-invalid={
                                (leg.manualKm.trim() !== '' && kmInput(leg.manualKm) === null) ||
                                undefined
                              }
                            />
                            <span className="text-muted-foreground">km</span>
                          </div>
                          <Input
                            aria-label={`Motivo do km à mão (${QUOTE_LEG_LABELS[key].toLowerCase()})`}
                            value={leg.reason}
                            onChange={(e) => setLeg(key, { reason: e.target.value })}
                            placeholder="Motivo (obrigatório)"
                            className="h-8"
                          />
                        </div>
                      ) : r && routeFresh(key) ? (
                        <div>
                          <span className="font-medium tabular-nums">{formatKm(r.km)}</span>
                          <span className="text-xs text-muted-foreground">
                            {' '}
                            · {formatDuration(r.seconds)} · {r.meters.toLocaleString('pt-BR')} m
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {r ? 'Endereço alterado — calcule de novo' : 'Calcule para ver o km'}
                        </span>
                      )}
                      {km !== null && isLongLeg(km) ? (
                        <p
                          className="mt-1 flex items-start gap-1 text-xs text-[#AD6800]"
                          data-testid={`quote-long-leg-${key}`}
                        >
                          <TriangleAlert className="mt-0.5 size-3 shrink-0" />
                          {QUOTE_LONG_LEG_WARNING}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5">
                      <MoneyInput
                        id={`quote-price-${key}`}
                        value={leg.price}
                        onChange={(price) => setLeg(key, { price })}
                        // com o valor cheio, vazio é "não informado", e não R$ 0,00
                        placeholder={fullMode ? 'opcional' : undefined}
                      />
                    </td>
                    <td
                      className={
                        fullMode
                          ? 'px-3 py-2.5 text-right tabular-nums text-muted-foreground'
                          : 'px-3 py-2.5 text-right font-medium tabular-nums'
                      }
                    >
                      {cents === null ? '—' : formatBRL(centsToMoney(cents))}
                      {fullMode && cents !== null ? (
                        <div className="text-xs font-normal">referência</div>
                      ) : null}
                      {problems[i] ? (
                        <div className="text-xs font-normal text-muted-foreground">
                          {problems[i]}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/30 align-top">
                <td colSpan={fullAmountAvailable ? 2 : 3} className="px-3 py-2.5">
                  <div className="font-semibold">Total (ida + volta)</div>
                  {fullAmountAvailable ? (
                    <div className="text-xs text-muted-foreground" data-testid="quote-total-mode">
                      {fullMode ? 'valor cheio digitado' : 'soma por km'}
                    </div>
                  ) : null}
                </td>
                {fullAmountAvailable ? (
                  <td className="px-3 py-2.5">
                    <Label htmlFor="quote-full-amount" className="mb-1 text-xs">
                      Valor cheio (opcional)
                    </Label>
                    <MoneyInput id="quote-full-amount" value={fullText} onChange={setFullText} />
                    {fullProblem ? (
                      <div
                        className="mt-1 text-xs text-destructive"
                        data-testid="quote-full-amount-problem"
                      >
                        {fullProblem}
                      </div>
                    ) : null}
                  </td>
                ) : null}
                <td
                  className="px-3 py-2.5 text-right font-semibold tabular-nums"
                  data-testid="quote-total"
                >
                  {totalCents === null ? '—' : formatBRL(centsToMoney(totalCents))}
                </td>
              </tr>
              {fullAmountAvailable ? (
                <tr className="bg-muted/30">
                  <td
                    colSpan={4}
                    className="px-3 pb-2.5 text-xs text-muted-foreground"
                    data-testid="quote-full-amount-hint"
                  >
                    {fullMode ? (
                      <>
                        <strong className="font-semibold text-foreground">
                          Valor cheio digitado: é o total da cotação
                        </strong>{' '}
                        e vira um lançamento só. O preço por km é opcional e fica só como referência
                        {perKmCents !== null ? (
                          <span data-testid="quote-per-km-reference">
                            {' '}
                            (pelo km daria {formatBRL(centsToMoney(perKmCents))})
                          </span>
                        ) : null}
                        : mudar o preço por km não muda este total. Apague o valor cheio para voltar
                        a cobrar por km.
                      </>
                    ) : (
                      <>
                        Prefere lançar o valor do atendimento direto? Digite o valor cheio do
                        deslocamento (ida e volta) acima: ele vira o total, e o preço por km fica
                        opcional.
                      </>
                    )}
                  </td>
                </tr>
              ) : null}
            </tfoot>
          </table>
        </div>

        {create.error ? (
          <p className="text-sm text-destructive">{quoteErrorMessage(create.error)}</p>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <p className="mr-auto text-xs text-muted-foreground" data-testid="quote-launch-note">
            {fullMode
              ? 'Lançar grava a cotação e cria um lançamento só, com o valor cheio (ida e volta), na aba Valores.'
              : 'Lançar grava a cotação e cria um lançamento por perna (km × preço por km) na aba Valores.'}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" size="sm" onClick={onLaunch} disabled={busy || !canLaunch}>
            {create.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Lançar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

const QUOTE_POINT_KEYS: QuotePointKey[] = ['origin', 'destination', 'returnTo'];
const QUOTE_LEG_KEYS: QuoteLegKey[] = ['ida', 'volta'];

function PointField({
  pointKey,
  label,
  placeholder,
  point,
  onText,
  onPick,
  suggest,
  onUnavailable,
  mapsAvailable,
}: {
  pointKey: QuotePointKey;
  label: string;
  placeholder: string;
  point: PointState;
  onText: (text: string) => void;
  onPick: (choice: QuotePlaceChoice) => void;
  suggest: SuggestFn | null;
  onUnavailable: () => void;
  mapsAvailable: boolean;
}) {
  const id = `quote-point-${pointKey}`;
  const changed = point.resolvedText !== null && point.text.trim() !== point.resolvedText;
  // escolhida e ainda não posicionada (o "Calcular" de sucesso limpa a escolha)
  const picked = chosen(point);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-1.5">
        <span
          className="inline-flex size-5 items-center justify-center rounded-full text-[11px] font-semibold text-white"
          style={{ background: QUOTE_POINT_COLORS[pointKey] }}
        >
          {QUOTE_POINT_LETTERS[pointKey]}
        </span>
        {label}
      </Label>
      <AddressInput
        id={id}
        value={point.text}
        placeholder={placeholder}
        onText={onText}
        onPick={onPick}
        suggest={suggest}
        onUnavailable={onUnavailable}
      />
      {picked ? (
        <p
          className="flex items-start gap-1 text-xs text-muted-foreground"
          data-testid={`${id}-escolhido`}
        >
          <MapPin className="mt-0.5 size-3 shrink-0" />
          Escolhido da lista — clique em Calcular para posicionar.
        </p>
      ) : mapsAvailable && point.address && !changed ? (
        <div className="space-y-1 text-xs">
          <p className="flex items-start gap-1 text-muted-foreground">
            <MapPin className="mt-0.5 size-3 shrink-0" />
            <span>
              {point.address}
              {point.precision ? (
                <Badge
                  variant={
                    point.precision === 'center' || point.precision === 'approximate'
                      ? 'warning'
                      : 'default'
                  }
                  className="ml-1.5 align-middle"
                >
                  {GEO_PRECISION_LABELS[point.precision]}
                </Badge>
              ) : null}
            </span>
          </p>
          {point.warnings.map((w) => (
            <p key={w} className="flex items-start gap-1 text-[#AD6800]">
              <TriangleAlert className="mt-0.5 size-3 shrink-0" />
              {QUOTE_POINT_WARNING_LABELS[w]}
            </p>
          ))}
        </div>
      ) : changed ? (
        <p className="text-xs text-muted-foreground">
          Endereço alterado — clique em Calcular para achar a nova posição.
        </p>
      ) : null}
    </div>
  );
}
